import {authFetch} from "../../authentication/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

function normalizePracticeOption(option, index) {
    if (!option) return null;

    const text = option.text ?? option.label ?? option.value ?? '';
    const isCorrect = option.isCorrect ?? option.is_correct ?? option.correct ?? false;

    return {
        ...option,
        id: option.id ?? option.optionId ?? `option-${index}`,
        position: option.position ?? option.order ?? index,
        text,
        label: text,
        value: text,
        isCorrect,
        is_correct: isCorrect,
        correct: isCorrect,
    };
}

function normalizePracticeItem(item, index) {
    if (!item) return null;

    const questionType = item.type ?? item.questionType ?? item.question_type ?? 'MULTIPLE_CHOICE';
    const allowMultipleAnswers = item.allowMultipleAnswers ?? item.allow_multiple_answers ?? false;
    const correctAnswer = item.correctAnswer ?? item.correct_answer ?? null;
    const expectedAnswer = item.expectedAnswer ?? item.expected_answer ?? '';
    const explanation = item.explanation ?? '';
    const language = item.language ?? item.codeLanguage ?? item.code_language ?? null;
    const codingType = item.codingType ?? item.coding_type ?? null;
    const options = (item.options ?? item.choiceOptions ?? item.choices ?? []).map(normalizePracticeOption).filter(Boolean);
    const guidedSteps = (item.guidedSteps ?? item.guided_steps ?? []).map((step, stepIndex) => ({
        ...step,
        id: step.id ?? `guided-step-${stepIndex}`,
        position: step.position ?? stepIndex,
        instruction: step.instruction ?? '',
        validation: step.validation ?? step.expectedAnswer ?? step.expected_answer ?? '',
    }));

    return {
        ...item,
        id: item.id ?? item.practiceItemId ?? `item-${index}`,
        position: item.position ?? index,
        type: questionType,
        questionType,
        question_type: questionType,
        allowMultipleAnswers,
        allow_multiple_answers: allowMultipleAnswers,
        correctAnswer,
        correct_answer: correctAnswer,
        expectedAnswer,
        expected_answer: expectedAnswer,
        explanation,
        language,
        codeLanguage: language,
        code_language: language,
        codingType,
        coding_type: codingType,
        options,
        choiceOptions: options,
        choices: options,
        guidedSteps,
        guided_steps: guidedSteps,
    };
}

export function normalizePracticeSet(practiceSet) {
    if (!practiceSet) return null;

    const userId = practiceSet.userId ?? practiceSet.user_id ?? null;
    const projectId = practiceSet.projectId ?? practiceSet.project_id ?? null;
    const noteId = practiceSet.noteId ?? practiceSet.note_id ?? null;
    const createdAt = practiceSet.createdAt ?? practiceSet.created_at ?? null;
    const updatedAt = practiceSet.updatedAt ?? practiceSet.updated_at ?? null;
    const practiceItems = (practiceSet.practiceItems ?? practiceSet.practice_items ?? []).map(normalizePracticeItem).filter(Boolean);

    return {
        ...practiceSet,
        userId,
        user_id: userId,
        projectId,
        project_id: projectId,
        noteId,
        note_id: noteId,
        createdAt,
        created_at: createdAt,
        updatedAt,
        updated_at: updatedAt,
        practiceItems,
        practice_items: practiceItems,
    };
}

export const generateNoteQuiz = async (token, noteId) => {
    if (!token || !noteId) throw new Error('Missing note id or auth token');

    const response = await authFetch(`${API_URL}/api/note/${noteId}/quiz/generate`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || payload?.detail || 'Failed to generate AI quiz');
    }

    return normalizePracticeSet(await response.json());
};

export const generatePracticeSet = async (token, generationOptions) => {
    if (!token) throw new Error('Missing auth token');

    const response = await authFetch(`${API_URL}/api/practice-set/generate`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(generationOptions),
    });

    if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || payload?.detail || 'Failed to generate practice set');
    }

    return normalizePracticeSet(await response.json());
};

export const fetchPracticeGenerationOptions = async (token) => {
    if (!token) return {categories: []};

    const response = await authFetch(`${API_URL}/api/practice-set/generation-options`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to fetch practice generation options');

    const payload = await response.json();
    return {
        categories: Array.isArray(payload?.categories) ? payload.categories : [],
    };
};

function serializePracticeOption(option) {
    return {
        position: option.position ?? 0,
        text: option.text ?? '',
        isCorrect: option.isCorrect ?? false,
        correct: option.isCorrect ?? false,
    };
}

function serializePracticeItem(item, index) {
    const questionType = item.questionType ?? item.question_type ?? 'MULTIPLE_CHOICE';
    const allowMultipleAnswers = item.allowMultipleAnswers ?? item.allow_multiple_answers ?? false;
    const language = item.language ?? item.codeLanguage ?? item.code_language ?? null;
    const codingType = item.codingType ?? item.coding_type ?? null;
    const options = (item.options ?? item.choiceOptions ?? item.choices ?? []).map(serializePracticeOption);
    const guidedSteps = (item.guidedSteps ?? item.guided_steps ?? []).map((step, stepIndex) => ({
        position: step.position ?? stepIndex,
        instruction: step.instruction ?? '',
        validation: step.validation ?? '',
    }));

    return {
        position: item.position ?? index,
        type: questionType,
        questionType,
        prompt: item.prompt ?? '',
        explanation: item.explanation ?? '',
        allowMultipleAnswers,
        correctAnswer: item.correctAnswer ?? item.correct_answer ?? null,
        expectedAnswer: item.expectedAnswer ?? item.expected_answer ?? '',
        language,
        codingType,
        coding_type: codingType,
        options,
        guidedSteps,
    };
}

function serializePracticeSet(practiceSet) {
    const practiceItems = (practiceSet.practiceItems ?? practiceSet.practice_items ?? []).map(serializePracticeItem);

    return {
        user_id: practiceSet.userId ?? practiceSet.user_id ?? null,
        title: practiceSet.title ?? '',
        description: practiceSet.description ?? '',
        project_id: practiceSet.projectId ?? practiceSet.project_id ?? null,
        note_id: practiceSet.noteId ?? practiceSet.note_id ?? null,
        language: practiceSet.language ?? null,
        practiceItems,
    };
}

export const fetchPracticeSets = async (token) => {
    if (!token) return [];

    const response = await authFetch(`${API_URL}/api/practice-sets`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to fetch practice sets');

    const payload = await response.json();
    const practiceSets = Array.isArray(payload) ? payload : payload?.items ?? [];
    return practiceSets.map(normalizePracticeSet).filter(Boolean);
};

export const fetchPracticeSet = async (token, id) => {
    if (!token || !id) return null;

    const response = await authFetch(`${API_URL}/api/practice-set/${id}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to fetch practice set');

    return normalizePracticeSet(await response.json());
};

export const createPracticeSet = async (token, practiceSet) => {
    if (!token) throw new Error('Missing auth token');

    const response = await authFetch(`${API_URL}/api/practice-set`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(serializePracticeSet(practiceSet)),
    });

    if (!response.ok) throw new Error('Failed to create practice set');

    return normalizePracticeSet(await response.json());
};

export const updatePracticeSet = async (token, practiceSetId, practiceSet) => {
    if (!token || !practiceSetId) throw new Error('Missing practice set id or auth token');

    const response = await authFetch(`${API_URL}/api/practice-set/${practiceSetId}`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            ...serializePracticeSet(practiceSet),
            id: practiceSetId,
        }),
    });

    if (!response.ok) throw new Error('Failed to update practice set');

    return normalizePracticeSet(await response.json());
};

async function parseMaybeJson(response) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        return response.json();
    }

    const text = await response.text();
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

export const deletePracticeSet = async (token, practiceSetId) => {
    if (!token || !practiceSetId) throw new Error('Missing practice set id or auth token');

    const response = await authFetch(`${API_URL}/api/practice-set/${practiceSetId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to delete practice set');

    return parseMaybeJson(response);
};
